import React, { FC } from "react";

import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { Typography } from "@mui/material";

import { NumberBadge } from "@/components/atoms";
import { Todo, TodoCategory } from "@/shared/domain";
import { useNavigation, useToast } from "@/shared/hooks";

import { StyledTodoCard } from "./styled";

type ToDosCardProps = {
    toDo: Todo;
    disabled?: boolean;
};

const ToDosCard: FC<ToDosCardProps> = ({ toDo, disabled }) => {
    const { errorToast } = useToast();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const {
        navToCustomerCard,
        navToLiability,
        navToCommunicationSalary,
        navToPackagesToSignList,
        navToTariffication,
        navToDataVerification,
    } = useNavigation();

    const showError = () => {
        errorToast({
            message: t("Errors.Todo", { todo: toDo.toDoType.name.toLowerCase() }),
            anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        queryClient.invalidateQueries("todos");
    };
    const withError = toDo.faulted;

    const handleClickToDo = () => {
        if (withError) return showError();
        const policyholders = toDo.detailsPerPolicyHolder
            .filter((it) => it.totalItems > 0)
            .reduce((acc, curr) => [...acc, curr.policyHolderId], []);
        switch (toDo.toDoType.id) {
            case TodoCategory.CUSTOMER_CARD:
                return navToCustomerCard();
            case TodoCategory.PACKAGES_TO_SIGN:
                return navToPackagesToSignList();
            case TodoCategory.LIABILITY:
                return navToLiability();
            case TodoCategory.COMMUNICATION_SALARY:
                return navToCommunicationSalary();
            case TodoCategory.TARIFFICATION:
                return navToTariffication(policyholders);
            case TodoCategory.DATA_VERIFICATION:
                return navToDataVerification(policyholders);
            default:
                return null;
        }
    };
    const handleKeyDown = (ev: React.KeyboardEvent) => {
        if (ev.key === "Enter") {
            handleClickToDo();
        }
    };

    const totalItems = toDo.detailsPerPolicyHolder.reduce((acc, detail) => acc + detail.totalItems, 0);

    return (
        <StyledTodoCard
            tabIndex={0}
            data-testid={`to_dos_card-button-${toDo.toDoType.id}`}
            onClick={handleClickToDo}
            onKeyDown={handleKeyDown}
            disabled={disabled}
        >
            <Typography fontSize={{ xs: "var(--size-14)", md: "var(--size-16)" }} noWrap color="var(--color-grey-800)">
                {toDo.toDoType.name}
            </Typography>
            {!withError && <NumberBadge value={totalItems} />}
        </StyledTodoCard>
    );
};

export default ToDosCard;
